import { inject } from '@angular/core';
import {
    HttpErrorResponse,
    HttpHandlerFn, HttpInterceptorFn,
    HttpRequest,
    HttpResponse
} from '@angular/common/http';
import { retry, throwError, timer } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { AlertService, AppVersionService } from '@app/services';
import { Router } from '@angular/router';
import { RouteRepository } from '@app/repositories';
import * as Sentry from '@sentry/capacitor';
import { Network } from '@capacitor/network';

export const responseInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) =>
{
    const routeRepository = inject(RouteRepository);
    const alertService = inject(AlertService);
    const appVersionService = inject(AppVersionService);
    const router = inject(Router);
    const retryMaxCount: number = 3;

    return next(req.clone()).pipe(
        retry({
            count: retryMaxCount - 1, // for some reason first request does not count
            resetOnSuccess: false,
            delay: (error) =>
            {
                // retry ONLY if status code NOT 500
                // if status code is 500 or more - throw an error and do NOT retry
                if (error instanceof HttpErrorResponse && error.status < 500)
                {
                    return throwError(() => error);
                }

                return timer(1000);
            }
        }),
        map((response) =>
        {
            if (response instanceof HttpResponse)
            {
                appVersionService.setLatestVersion(response.headers.get('X-App-Latest-Version'));
                // alertService.show(response.body.message);
            }

            return response;
        }),
        catchError((response) =>
        {
            if (response instanceof HttpErrorResponse)
            {
                appVersionService.setLatestVersion(response.headers.get('X-App-Latest-Version'));

                if (response.error !== null)
                {
                    if (response.error.hasOwnProperty('message'))
                    {
                        if (response.status === 404)
                        {
                            console.log(response.error.message);
                        }
                        else
                        {
                            alertService.show(response.error.message);
                        }
                    }

                    if (response.error.hasOwnProperty('errors'))
                    {
                        alertService.showResponseError(response);
                    }
                }

                if (response.status === 401)
                {
                    routeRepository.getActiveRoute().then(async (activeRoute) =>
                    {
                        const errorMessage = response.error?.message ?? response.message ?? 'Unauthorized';
                        const url = req.urlWithParams;
                        const networkStatus = await Network.getStatus();

                        Sentry.withScope((scope) =>
                        {
                            scope.setLevel('warning');
                            scope.setTag('status_code', '401');
                            scope.setTag('url', url);
                            scope.setTag('connection_type', networkStatus.connectionType);

                            const extra: any = {
                                url,
                                error_message: errorMessage,
                                response_body: response.error,
                                network_status: networkStatus,
                            };

                            if (activeRoute)
                            {
                                extra.active_route = {
                                    id: activeRoute.id,
                                    external_id: activeRoute.external_id,
                                    name: activeRoute.name,
                                };
                                scope.setTag('active_route_id', activeRoute.id.toString());
                            }

                            Sentry.captureMessage(`401 Unauthorized: ${url}`, {
                                extra,
                                level: 'warning',
                            });
                        });

                        if (activeRoute === null)
                        {
                            await router.navigate(['login'], { replaceUrl: true, });
                        }
                    });
                }
            }

            return throwError(() => response);
        }),
    );
};
